import React, { useState , useRef , useEffect } from "react";
import Chip from "@mui/material/Chip";
import {
  ClickAwayListener,
  List,
  ListItem,
  ListItemText,
  Popover,
} from "@mui/material";
import GetIcon from "@/components/font-awesome-icon";
import { faEllipsis } from "@fortawesome/free-solid-svg-icons";


import TruncateTooltip from "@/components/truncate-with-tooltip";
import { useSelector } from "react-redux";
import { getTagColors } from "@/store/slices/general/dropdownOptionsSlice";

function TagsChips({ tags }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const containerRef = useRef(null);
  const [visibleTags, setVisibleTags] = useState([]);
  const [hiddenTags, setHiddenTags] = useState([]);
  const [showEllipsis, setShowEllipsis] = useState(false);
  const tagColors = useSelector(getTagColors);
  useEffect(() => {
    const container = containerRef.current;

    const updateTags = () => {
      if (!container) return;
      const newWidth = container.offsetWidth;
      if (newWidth > 0) {
        updateVisibleTags(newWidth);
      }
    };

    updateTags();

    // Create a ResizeObserver instance
    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        if (entry.target === container && tags?.length) {
          updateTags();
        }
      }
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [containerRef, tags]);

  const updateVisibleTags = (parentWidth) => {
    const tagsToShow = calculateMaxChipsInDiv(parentWidth, tags);
    const newVisibleTags = tags.slice(0, tagsToShow);
    const newHiddenTags = tags.slice(tagsToShow);

    if (newHiddenTags.length > 0) {
      // Make space for ... chip in visible tags
      if (newVisibleTags?.length > 1) {
        const tagToHide = newVisibleTags.pop();
        newHiddenTags.unshift(tagToHide);
      }
      setShowEllipsis(true);
    } else {
      setShowEllipsis(false);
    }

    setVisibleTags(newVisibleTags);
    setHiddenTags(newHiddenTags);
  };

  const calculateMaxChipsInDiv = (divWidth) => {
    const extraSpace = 8; // Width for a chip without text
    let totalWidth = 0;

    for (let i = 0; i < tags.length; i++) {
      const chipWidth =
        tags[i]?.name?.substring(0, 15)?.length * 8 + extraSpace;
      totalWidth += chipWidth;

      if (totalWidth > divWidth) {
        return i; // return when the div width is exceeded
      }
    }

    return tags.length;
  };

  const handleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setAnchorEl(null);
  };

  return (
    <div ref={containerRef} style={{ width: "100%" }}>
      {visibleTags.map((tag) => {
        let color = tagColors.find((t) => t.id == tag?.tagColorId);
        return (
          <Chip
            key={tag?.name}
            label={<TruncateTooltip text={tag?.name} limit={15} />}
            style={{
              backgroundColor: color?.code,
              color: color?.textCode,
            }}
          />
        );
      })}{" "}
      {showEllipsis && (
        <>
          <ClickAwayListener onClickAway={handleClose}>
            <Chip
              // label="..."
              label={<GetIcon icon={faEllipsis} />}
              onClick={handleClick}
              style={{ cursor: "pointer" }}
            />
          </ClickAwayListener>
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleClose}
            PaperProps={{
              style: {
                width: "180px",
                maxHeight: "280px",
              },
            }}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <div>
              <List>
                {hiddenTags.map((tag, index) => {
                  let color = tagColors.find((t) => t.id == tag?.tagColorId);
                  return (
                    <ListItem key={index} sx={{ py: 0 }} onClick={handleClose}>
                      <ListItemText>
                        <Chip
                          key={tag.name}
                          label={
                            <TruncateTooltip text={tag?.name} limit={15} />
                          }
                          style={{
                            backgroundColor: color.code,
                            color: color?.textCode,
                          }}
                        />
                      </ListItemText>
                    </ListItem>
                  );
                })}
              </List>
            </div>
          </Popover>
        </>
      )}
    </div>
  );
}

export default TagsChips;
